import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Box,
  Paper,
  Divider,
  CircularProgress,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { toast } from 'react-toastify';

// Define field mapping information
const exportFields = {
  title: { name: 'Title', description: 'Project title', defaultSelected: true },
  description: {
    name: 'Description',
    description: 'Project detailed description',
    defaultSelected: true,
  },
  type: {
    name: 'Type',
    description: 'Project type (Academic, Research, etc.)',
    defaultSelected: true,
  },
  progress_status: {
    name: 'Status',
    description: 'Current progress status',
    defaultSelected: true,
  },
  student: {
    name: 'Owner',
    description: 'Project owner/student',
    defaultSelected: true,
  },
  teammates: {
    name: 'Teammates',
    description: 'Project team members',
    defaultSelected: true,
  },
  documents: {
    name: 'Documents',
    description: 'Project document links',
    defaultSelected: false,
  },
  links: {
    name: 'Links',
    description: 'Project external links',
    defaultSelected: false,
  },
  platform: {
    name: 'Platform',
    description: 'Platform for the project',
    defaultSelected: false,
  },
  expenditure: {
    name: 'Expenditure',
    description: 'Amount spent on the project',
    defaultSelected: false,
  },
  budget: {
    name: 'Budget',
    description: 'Project budget',
    defaultSelected: false,
  },
  duration: {
    name: 'Duration',
    description: 'Project duration in months',
    defaultSelected: false,
  },
  is_patent_filed: {
    name: 'Patent Filed',
    description: 'Whether a patent has been filed',
    defaultSelected: false,
  },
  mentors: {
    name: 'Mentors',
    description: 'Project mentors',
    defaultSelected: false,
  },
  created_at: {
    name: 'Created Date',
    description: 'Project creation date',
    defaultSelected: false,
  },
  updated_at: {
    name: 'Last Updated',
    description: 'Last update date',
    defaultSelected: false,
  },
};

// Mapping for type values
const typeMapping = {
  1: 'Academic',
  2: 'Research',
  3: 'Personal',
  4: 'Other',
};

// Mapping for progress status values
const progressStatusMapping = {
  1: 'Not Started',
  2: 'In Progress',
  3: 'Completed',
};

// Mapping for platform values
const platformMapping = {
  1: 'Competitions',
  2: 'Exhibitions',
  3: 'Industry',
};

const ProjectExport = ({ projects, users }) => {
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [selectedFields, setSelectedFields] = useState(
    Object.fromEntries(
      Object.entries(exportFields).map(([key, field]) => [
        key,
        field.defaultSelected,
      ])
    )
  );
  const [isExporting, setIsExporting] = useState(false);

  // Handler for opening the export dialog
  const handleOpenExportDialog = () => {
    setIsExportDialogOpen(true);
  };

  // Handler for closing the export dialog
  const handleCloseExportDialog = () => {
    setIsExportDialogOpen(false);
  };

  // Toggle selection of a field
  const handleFieldToggle = field => {
    setSelectedFields(prev => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  // Select all fields
  const handleSelectAll = () => {
    setSelectedFields(
      Object.fromEntries(Object.keys(exportFields).map(key => [key, true]))
    );
  };

  // Deselect all fields
  const handleDeselectAll = () => {
    setSelectedFields(
      Object.fromEntries(Object.keys(exportFields).map(key => [key, false]))
    );
  };

  // Convert datetime string to readable format
  const formatDate = dateString => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Get user name by ID
  const getUserName = userId => {
    const user = users.find(u => u.id === userId);
    return user
      ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
      : 'Unknown';
  };

  // Format project data for export
  const formatProjectDataForExport = project => {
    const formattedData = {};

    if (selectedFields.title) formattedData.Title = project.title || '';
    if (selectedFields.description)
      formattedData.Description = project.description || '';

    if (selectedFields.type) {
      formattedData.Type = typeMapping[project.type] || project.type || '';
    }

    if (selectedFields.progress_status) {
      formattedData.Status =
        progressStatusMapping[project.progress_status] ||
        project.progress_status ||
        '';
    }

    if (selectedFields.student) {
      const studentName = getUserName(project.student);
      formattedData.Owner = studentName;
    }

    if (selectedFields.teammates && project.teammates) {
      const teammateNames = project.teammates
        .map(teammateId => getUserName(teammateId))
        .filter(name => name !== 'Unknown')
        .join(', ');
      formattedData.Teammates = teammateNames;
    }

    if (selectedFields.documents)
      formattedData.Documents = project.documents || '';
    if (selectedFields.links) formattedData.Links = project.links || '';

    if (selectedFields.platform) {
      formattedData.Platform =
        platformMapping[project.platform] || project.platform || '';
    }

    if (selectedFields.expenditure)
      formattedData.Expenditure = project.expenditure || '';
    if (selectedFields.budget) formattedData.Budget = project.budget || '';
    if (selectedFields.duration)
      formattedData.Duration = project.duration || '';

    if (selectedFields.is_patent_filed) {
      formattedData['Patent Filed'] = project.is_patent_filed ? 'Yes' : 'No';
    }

    if (selectedFields.mentors) formattedData.Mentors = project.mentors || '';
    if (selectedFields.created_at)
      formattedData['Created Date'] = formatDate(project.created_at);
    if (selectedFields.updated_at)
      formattedData['Last Updated'] = formatDate(project.updated_at);

    return formattedData;
  };

  // Handle the export process
  const handleExport = () => {
    if (Object.values(selectedFields).every(val => !val)) {
      toast.error('Please select at least one field to export');
      return;
    }

    setIsExporting(true);

    try {
      // Format all projects
      const formattedProjects = projects.map(formatProjectDataForExport);

      if (formattedProjects.length === 0) {
        toast.warning('No projects to export');
        setIsExporting(false);
        return;
      }

      // Get headers from the first project
      const headers = Object.keys(formattedProjects[0]);

      // Create CSV content - use proper newlines instead of string literals
      let csvContent = headers.join(',') + '\n';

      // Add data rows
      formattedProjects.forEach(project => {
        const row = headers.map(header => {
          // Handle fields that might contain commas or quotes
          const cell = String(project[header] || '');
          if (cell.includes(',') || cell.includes('"') || cell.includes('\n')) {
            return `"${cell.replace(/"/g, '""')}"`;
          }
          return cell;
        });
        csvContent += row.join(',') + '\n';
      });

      // Create and download the file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute(
        'download',
        `projects_export_${new Date().toISOString().split('T')[0]}.csv`
      );
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setIsExporting(false);
      setIsExportDialogOpen(false);
      toast.success('Projects exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export projects');
      setIsExporting(false);
    }
  };

  // Count selected fields
  const selectedFieldCount =
    Object.values(selectedFields).filter(Boolean).length;

  return (
    <>
      <Button
        variant="outlined"
        color="primary"
        onClick={handleOpenExportDialog}
        startIcon={<FileDownloadIcon />}
        sx={{
          borderRadius: '8px',
          textTransform: 'none',
          fontWeight: 500,
          '&:hover': {
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          },
        }}
      >
        Export
      </Button>

      <Dialog
        open={isExportDialogOpen}
        onClose={handleCloseExportDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: { borderRadius: '10px' },
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid #eee', pb: 1 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6" fontWeight={500}>
              Export Projects to CSV
            </Typography>
            <Chip
              label={`${selectedFieldCount} fields selected`}
              color="primary"
              size="small"
            />
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body1" gutterBottom>
            Select the fields you want to include in the export:
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, mb: 2, mt: 1 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={handleSelectAll}
              sx={{ textTransform: 'none' }}
            >
              Select All
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={handleDeselectAll}
              sx={{ textTransform: 'none' }}
            >
              Deselect All
            </Button>
          </Box>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: '8px' }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Project Fields
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(1, 1fr)',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)',
                },
                gap: 2,
              }}
            >
              {Object.entries(exportFields).map(([key, field]) => (
                <FormGroup key={key}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedFields[key] || false}
                        onChange={() => handleFieldToggle(key)}
                        color="primary"
                        size="small"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2">{field.name}</Typography>
                        <Tooltip title={field.description} arrow>
                          <IconButton size="small">
                            <HelpOutlineIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    }
                  />
                </FormGroup>
              ))}
            </Box>
          </Paper>

          <Box sx={{ mt: 3, display: 'flex', alignItems: 'center' }}>
            <CheckCircleIcon fontSize="small" color="success" sx={{ mr: 1 }} />
            <Typography variant="body2" color="text.secondary">
              {projects.length} project{projects.length !== 1 ? 's' : ''} will
              be exported
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ borderTop: '1px solid #eee', p: 2 }}>
          <Button
            onClick={handleCloseExportDialog}
            color="inherit"
            disabled={isExporting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleExport}
            variant="contained"
            color="primary"
            disabled={isExporting || selectedFieldCount === 0}
            startIcon={
              isExporting ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <FileDownloadIcon />
              )
            }
          >
            {isExporting ? 'Exporting...' : 'Export CSV'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProjectExport;
